<template>
  <div class="flex flex-col gap-6">
    <!-- Company Name Toggle: [My Company] / Other Company -->
    <div>
      <b-label class="mb-2 block">{{ $t('publish_mission.step1.company_toggle_label') }}</b-label>
      <div class="flex items-center gap-2 bg-gray-800 rounded-lg w-full p-1">
        <button
          type="button"
          @click="useOwnCompany = true"
          class="flex-1 text-center px-4 py-2 text-sm rounded-md transition-colors"
          :class="[useOwnCompany ? 'bg-white text-black font-semibold' : 'text-gray-400']"
          data-e2e="step1-company-own"
        >
          {{ $t('publish_mission.step1.my_company', { companyName: userCompanyName || t('publish_mission.step1.default_company_name') }) }}
        </button>
        <button
          type="button"
          @click="useOwnCompany = false"
          class="flex-1 text-center px-4 py-2 text-sm rounded-md transition-colors"
          :class="[!useOwnCompany ? 'bg-white text-black font-semibold' : 'text-gray-400']"
          data-e2e="step1-company-other"
        >
          {{ $t('publish_mission.step1.other_company') }}
        </button>
      </div>
    </div>

    <!-- Block own-company mode when the company identity is still "[NON DIFFUSIBLE]":
         the user must fix their real name/address before creating a mission. -->
    <b-alert
      v-if="useOwnCompany && ownCompanyNonDiffusible"
      variant="destructive"
      :title="$t('publish_mission.step1.non_diffusible_title')"
      :description="$t('publish_mission.step1.non_diffusible_description')"
      data-e2e="step1-non-diffusible-block"
    >
      <template #actions>
        <nuxt-link
          to="/settings/company"
          class="inline-flex items-center rounded-lg bg-white px-4 py-2 text-sm font-semibold text-black transition-colors hover:bg-gray-200"
          data-e2e="step1-non-diffusible-cta"
        >
          {{ $t('publish_mission.step1.non_diffusible_cta') }}
        </nuxt-link>
      </template>
    </b-alert>

    <!-- External Company Search (when "Other Company" selected) -->
    <template v-if="!useOwnCompany">
      <b-combobox
        :label="$t('publish_mission.step1.external_company_label')"
        :placeholder="$t('publish_mission.step1.external_company_placeholder')"
        v-model="selectedExternalCompany"
        :items="externalCompaniesItems"
        :min-search-length="2"
        @search="onExternalCompanySearch"
        data-e2e="step1-external-company-combobox"
      />

      <b-text-field
        v-model="externalContactEmail"
        :label="$t('publish_mission.step1.contact_email_label')"
        :placeholder="$t('publish_mission.step1.contact_email_placeholder')"
        :readonly="isExternalEmailReadonly"
        :rules="(value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || $t('publish_mission.step1.invalid_email')"
        data-e2e="step1-contact-email-input"
      />
    </template>

    <!-- Company Type (chips) -->
    <div>
      <b-label class="mb-2 block">{{ companyTypeLabel }}</b-label>
      <RadioGroup v-model="selectedCompanyType" data-e2e="step1-company-type-group">
        <div class="flex flex-wrap gap-2">
          <RadioGroupOption
            v-for="companyType in companyTypeOptions"
            :key="companyType.value"
            v-slot="{ checked }"
            :value="companyType.value"
            :data-e2e="`step1-company-type-${companyType.value}`"
          >
            <BRadioChip :checked="checked">
              {{ companyType.label }}
            </BRadioChip>
          </RadioGroupOption>
        </div>
      </RadioGroup>
    </div>

    <!-- Industry Sector (dropdown) -->
    <b-select
      searchable
      :items="industryOptions"
      v-model="selectedIndustry"
      :label="$t('publish_mission.step1.industry_label')"
      :placeholder="$t('publish_mission.step1.industry_placeholder')"
      top
      data-e2e="step1-industry-select"
    />

    <!-- Target Market Segments (ICP company sizes targeted by the mission) -->
    <div>
      <b-label class="mb-2 block">{{ $t('publish_mission.step1.market_segments_label') }}</b-label>
      <b-checkbox-group v-model="selectedMarketSegments" data-e2e="step1-market-segments-group">
        <div class="flex flex-wrap gap-2">
          <b-checkbox-chip
            v-for="marketSegment in marketSegmentOptions"
            :key="marketSegment.value"
            :value="marketSegment.value"
            :data-e2e="`step1-market-segment-${marketSegment.value}`"
          >
            {{ marketSegment.label }}
          </b-checkbox-chip>
        </div>
      </b-checkbox-group>
    </div>

    <!-- Target Personas (max 3) - moved from step 2 -->
    <b-chip-input
      name="personas"
      searchable
      :items="personaOptions"
      v-model="selectedPersonas"
      :max-items="3"
      :label="personasLabel"
      :placeholder="$t('publish_mission.step1.personas_placeholder')"
      data-e2e="step1-personas-input"
    />

    <!-- Deal Size (chips) - moved from step 2 -->
    <div>
      <b-label class="mb-2 block">{{ $t('publish_mission.step1.deal_size_label') }}</b-label>
      <b-checkbox-group v-model="selectedDealSizes" data-e2e="step1-deal-size-group">
        <div class="flex flex-wrap gap-2">
          <b-checkbox-chip
            v-for="dealSize in dealSizeOptions"
            :key="dealSize.value"
            :value="dealSize.value"
            :data-e2e="`step1-deal-size-${dealSize.value}`"
          >
            {{ dealSize.label }}
          </b-checkbox-chip>
        </div>
      </b-checkbox-group>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { RadioGroup, RadioGroupOption } from '@headlessui/vue';
import BSelect from '~/components/forms/select/BSelect.vue';
import BLabel from '~/components/forms/BLabel.vue';
import BRadioChip from '~/components/forms/radio/BRadioChip.vue';
import BChipInput from '~/components/forms/chip-input/BChipInput.vue';
import BCheckboxGroup from '~/components/forms/checkbox/BCheckboxGroup.vue';
import BCheckboxChip from '~/components/forms/checkbox/BCheckboxChip.vue';
import BCombobox, { type ComboboxItem } from '~/components/forms/BCombobox.vue';
import BTextField from '~/components/forms/BTextField.vue';
import BAlert from '~/components/alert/BAlert.vue';
import { groupByCategory, toSelectOptions } from '~/utils/categoryGrouping';
import { useCompanyStore } from '~/stores/companyStore';
import { isNonDiffusible } from '~/help/nonDiffusible';

const { t } = useI18n();

interface Props {
  initialData?: Record<string, any>;
}

const props = defineProps<Props>();

const emit = defineEmits(['change']);

const companySizeStore = useCompanySizeStore();
const industryStore = useIndustryStore();
const personaStore = usePersonaStore();
const dealSizeStore = useDealSizeStore();
const marketSegmentStore = useMarketSegmentStore();
const companyStore = useCompanyStore();

const { companySizes } = storeToRefs(companySizeStore);
const { industries } = storeToRefs(industryStore);
const { personas } = storeToRefs(personaStore);
const { dealSizes } = storeToRefs(dealSizeStore);
const { marketSegments } = storeToRefs(marketSegmentStore);

const useOwnCompany = ref(true);
const selectedCompanyType = ref<string | null>(null);
const selectedIndustry = ref<{ value: string; label: string } | null>(null);
const selectedPersonas = ref<{ value: string; label: string }[]>([]);
const selectedDealSizes = ref<string[]>([]);
const selectedMarketSegments = ref<string[]>([]);

const selectedExternalCompany = ref<ComboboxItem | null>(null);
const externalCompaniesItems = ref<ComboboxItem[]>([]);
const externalContactEmail = ref('');
const isExternalEmailReadonly = ref(false);

const isRestoringData = ref(false);

const userCompanyName = computed(() => {
  return companyStore.company?.public_name || companyStore.company?.business_name || null;
});

// The user's own company still carries the INSEE "[NON DIFFUSIBLE]" marker
// (registered before the fix). When targeting their own company, they must fix
// its real name/address before creating a mission — the banner blocks step 1 and
// the wizard's canGoNext disables "Next" while this is true.
const ownCompanyNonDiffusible = computed(() =>
  isNonDiffusible(companyStore.company?.business_name) || isNonDiffusible(companyStore.company?.address)
);

// Labels switch between "your company" and "client company" wording depending on
// whether the mission is created for the user's own company or on behalf of another one.
const companyTypeLabel = computed(() =>
  useOwnCompany.value
    ? t('publish_mission.step1.company_type_label')
    : t('publish_mission.step1.company_type_label_client')
);

const personasLabel = computed(() =>
  useOwnCompany.value
    ? t('publish_mission.step1.personas_label')
    : t('publish_mission.step1.personas_label_client')
);

const companyTypeOptions = computed(() =>
  toSelectOptions(companySizes.value || [], t('modal_create_mission.people_label'))
);

const industryOptions = computed(() =>
  groupByCategory(industries.value || [])
);

const personaOptions = computed(() => groupByCategory(personas.value || []));

const dealSizeOptions = computed(() =>
  toSelectOptions(dealSizes.value || [], 'K€')
);

const marketSegmentOptions = computed(() =>
  toSelectOptions(marketSegments.value || [], t('modal_create_mission.employee_label'))
);

const onExternalCompanySearch = (query: string) => {
  companyStore.searchCompanies(query, 'external').then((response) => {
    if ('companies' in response) {
      externalCompaniesItems.value = response.companies.map((company) => {
        const sublabel = [company.postal_code, company.city].filter(Boolean).join(' ');
        return {
          value: company.siren,
          label: company.business_name,
          sublabel: sublabel || undefined,
          email: company.contact?.email,
        };
      });
    } else {
      externalCompaniesItems.value = [];
    }
  });
};

watch(selectedExternalCompany, (company) => {
  if (isRestoringData.value) return;
  if (company?.email) {
    externalContactEmail.value = company.email;
    isExternalEmailReadonly.value = true;
  } else {
    externalContactEmail.value = '';
    isExternalEmailReadonly.value = false;
  }
});

// Pre-fill from company data when available (only if not currently restoring and user hasn't selected yet)
const prefillFromCompany = () => {
  // Skip if we're in the middle of restoring from draft/initialData
  if (isRestoringData.value) return;

  // Only pre-fill when "My Company" is selected
  if (!useOwnCompany.value) return;

  const company = companyStore.company;
  if (!company) return;

  // Pre-fill company size (company_type) if not already selected
  if (company.company_size_id && !selectedCompanyType.value) {
    selectedCompanyType.value = company.company_size_id;
  }

  // Pre-fill industry if not already selected
  // Note: API returns industries with 'id' field, company stores 'industry_id'
  if (company.industry_id && !selectedIndustry.value && industries.value?.length > 0) {
    const matchingIndustry = industries.value.find(i => i.id === company.industry_id);
    if (matchingIndustry) {
      selectedIndustry.value = {
        value: matchingIndustry.id,
        label: matchingIndustry.name,
      };
    }
  }
};

watch(useOwnCompany, (isOwn) => {
  // Skip during draft restoration to avoid race condition
  if (isRestoringData.value) return;

  if (isOwn) {
    // Switching to "My Company" - clear external company data and re-apply prefill
    selectedExternalCompany.value = null;
    externalCompaniesItems.value = [];
    externalContactEmail.value = '';
    prefillFromCompany();
  } else {
    // Switching to "Other Company" - clear pre-filled values from own company
    selectedCompanyType.value = null;
    selectedIndustry.value = null;
  }
});

watch(() => props.initialData, (data) => {

  if (data && Object.keys(data).length > 0) {
    isRestoringData.value = true;
    if (data.use_own_company !== undefined) {
      useOwnCompany.value = data.use_own_company;
    }
    if (data.company_type) {
      selectedCompanyType.value = data.company_type;
    }
    if (data.industry) {
      selectedIndustry.value = { value: data.industry, label: data.industry_label || data.industry };
    }
    if (data.personas) {
      selectedPersonas.value = data.personas;
    }
    if (data.deal_sizes) {
      selectedDealSizes.value = data.deal_sizes;
    }
    if (data.market_segments) {
      selectedMarketSegments.value = data.market_segments;
    }
    if (data.siren && !data.use_own_company) {
      selectedExternalCompany.value = {
        value: data.siren,
        label: data.company_label || data.siren,
      };
    }
    if (data.contact_email) {
      externalContactEmail.value = data.contact_email;
    }
    if (data.contact_email_readonly) {
      isExternalEmailReadonly.value = true;
    }
    nextTick(() => {
      isRestoringData.value = false;
    });
  }
}, { immediate: true, deep: true });

watch([
  useOwnCompany,
  selectedCompanyType,
  selectedIndustry,
  selectedPersonas,
  selectedDealSizes,
  selectedMarketSegments,
  selectedExternalCompany,
  externalContactEmail,
], () => {
  if (isRestoringData.value) return;

  emit('change', {
    use_own_company: useOwnCompany.value,
    company_type: selectedCompanyType.value,
    industry: selectedIndustry.value?.value,
    industry_label: selectedIndustry.value?.label,
    personas: selectedPersonas.value,
    deal_sizes: selectedDealSizes.value,
    market_segments: selectedMarketSegments.value,
    siren: selectedExternalCompany.value?.value,
    company_label: selectedExternalCompany.value?.label,
    contact_email: externalContactEmail.value,
    contact_email_readonly: isExternalEmailReadonly.value,
  });
}, { deep: true });

// Watch for company data and reference data to be loaded, then pre-fill
// Only watch specific properties needed for pre-fill to avoid expensive deep comparisons
watch(
  () => [
    companyStore.company?.company_size_id,
    companyStore.company?.industry_id,
    companySizes.value?.length,
    industries.value?.length,
  ],
  () => {
    prefillFromCompany();
  }
);

onMounted(async () => {
  await Promise.all([
    companySizeStore.fetchCompanySizes(),
    industryStore.fetchIndustries(),
    personaStore.fetchPersonas(),
    dealSizeStore.fetchDealSizes(),
    marketSegmentStore.fetchMarketSegments(),
  ]);

  // Try to pre-fill after reference data is loaded
  prefillFromCompany();
});
</script>
